import React from 'react';
import { ArrowLeft, FileText, Shield } from 'lucide-react';

const CGUPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <button 
          onClick={onBack} 
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 font-medium transition"
        >
          <ArrowLeft size={18}/> Retour
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
            <FileText className="text-blue-600" size={32}/>
            Conditions Générales d'Utilisation et de Vente
          </h1>

          <p className="text-sm text-slate-500 mb-8">
            Dernière mise à jour : 12 décembre 2025
          </p>

          {/* ARTICLE 1 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 1 - Objet</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Les présentes Conditions Générales d'Utilisation et de Vente (ci-après « CGU/CGV ») régissent 
                l'accès et l'utilisation de la plateforme Ovrio (ci-après « la Plateforme »), accessible à l'adresse 
                <strong> www.ovrio.net</strong>.
              </p>
              <p>
                Ovrio est un logiciel de gestion destiné aux professionnels du bâtiment, permettant la création 
                de devis, factures, le suivi de chantiers et la gestion de clients.
              </p>
              <p>
                L'utilisation de la Plateforme implique l'acceptation pleine et entière des présentes CGU/CGV.
              </p>
            </div>
          </section>

          {/* ARTICLE 2 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 2 - Offres et Tarifs</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <p>Ovrio propose trois formules d'abonnement :</p>
              
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3">
                <div>
                  <h3 className="font-bold text-slate-800 mb-1">🆓 Formule Gratuite</h3>
                  <ul className="list-disc list-inside ml-4 space-y-1 text-slate-600">
                    <li>Accès aux fonctionnalités de base</li>
                    <li>Création de devis et factures illimitée</li>
                    <li>Gestion des clients</li>
                    <li>Stockage illimité</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-bold text-slate-800 mb-1">⭐ Formule Pro - 10€/mois HT</h3>
                  <ul className="list-disc list-inside ml-4 space-y-1 text-slate-600">
                    <li>Toutes les fonctionnalités Gratuites</li>
                    <li>Signature électronique</li>
                    <li>Bibliothèque de prix</li>
                    <li>Gestion d'équipe</li>
                    <li>Support prioritaire</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-bold text-slate-800 mb-1">🚀 Formule Business - 15€/mois HT</h3>
                  <ul className="list-disc list-inside ml-4 space-y-1 text-slate-600">
                    <li>Toutes les fonctionnalités Pro</li>
                    <li>Suivi de chantiers illimité</li>
                    <li>Photos et rapports de chantier</li>
                    <li>Gestion des dépenses</li>
                    <li>Planning équipe</li>
                  </ul>
                </div>
              </div>

              <p className="font-semibold text-blue-600 mt-4">
                🎁 Période d'essai gratuite de 7 jours pour les formules Pro et Business
              </p>

              <p>
                Les tarifs sont affichés en euros (€) hors taxes. La TVA applicable sera ajoutée au moment du paiement 
                selon la législation en vigueur.
              </p>
            </div>
          </section>

          {/* ARTICLE 3 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 3 - Modalités de Paiement</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Le paiement des abonnements s'effectue par carte bancaire via une plateforme de paiement sécurisée.
              </p>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-bold text-slate-800 mb-2">Facturation</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Abonnement mensuel :</strong> Facturation chaque mois à date anniversaire</li>
                  <li><strong>Abonnement annuel :</strong> Facturation annuelle avec engagement de 12 mois</li>
                  <li>Le premier prélèvement intervient immédiatement après la fin de la période d'essai</li>
                </ul>
              </div>

              <p>
                Une facture est automatiquement générée et envoyée par email à chaque prélèvement.
              </p>
            </div>
          </section>

          {/* ARTICLE 4 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 4 - Durée et Résiliation</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-bold text-slate-800 mb-2">Abonnement mensuel (sans engagement)</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Résiliation possible à tout moment depuis votre espace personnel</li>
                  <li>La résiliation prend effet à la fin du mois en cours déjà payé</li>
                  <li>Aucun remboursement au prorata du mois entamé</li>
                </ul>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h3 className="font-bold text-slate-800 mb-2">Abonnement annuel (avec engagement)</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Engagement ferme de 12 mois</li>
                  <li>Résiliation anticipée non autorisée sauf cas de force majeure</li>
                  <li>Renouvellement automatique pour 12 mois sauf résiliation 30 jours avant l'échéance</li>
                </ul>
              </div>

              <p className="font-semibold text-slate-800 mt-4">
                Conservation des données : Vos données sont conservées pendant 3 mois après la résiliation. 
                Passé ce délai, elles sont définitivement supprimées et ne peuvent plus être récupérées.
              </p>
            </div>
          </section>

          {/* ARTICLE 5 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 5 - Droit de Rétractation</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Conformément au Code de la consommation, vous disposez d'un délai de <strong>14 jours calendaires</strong> 
                à compter de la date de souscription pour exercer votre droit de rétractation sans avoir à justifier de motifs.
              </p>
              
              <p>
                Pour exercer ce droit, envoyez un email à <strong>contact@ovrio.net</strong> avec l'objet 
                « Rétractation - [Votre numéro de commande] ».
              </p>

              <p>
                En cas de rétractation dans les délais, vous serez intégralement remboursé sous 14 jours 
                par le même moyen de paiement utilisé lors de la souscription.
              </p>
            </div>
          </section>

          {/* ARTICLE 6 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 6 - Propriété des Données</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="font-bold text-green-800 mb-2">
                  ✓ Vous restez propriétaire à 100% de vos données
                </p>
                <p>
                  Toutes les données que vous créez sur Ovrio (clients, devis, factures, photos, documents) 
                  vous appartiennent exclusivement. Ovrio ne revendique aucun droit sur ces données.
                </p>
              </div>

              <p>
                Vous pouvez exporter l'intégralité de vos données à tout moment depuis votre espace personnel.
              </p>

              <p>
                Ovrio s'engage à ne jamais vendre, louer ou partager vos données avec des tiers à des fins commerciales.
              </p>
            </div>
          </section>

          {/* ARTICLE 7 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 7 - Disponibilité et Support</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-bold text-slate-800 mb-2">Disponibilité du service</h3>
                <p>
                  Ovrio s'engage à maintenir une disponibilité du service de <strong>99%</strong> du temps, 
                  hors opérations de maintenance planifiée.
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Les maintenances programmées sont annoncées au moins 48h à l'avance par email.
                </p>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h3 className="font-bold text-slate-800 mb-2">Support client</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Canal :</strong> Email uniquement (contact@ovrio.net)</li>
                  <li><strong>Délai de réponse :</strong> Maximum 48 heures ouvrées</li>
                  <li><strong>Horaires :</strong> Du lundi au vendredi, 9h-17h (hors jours fériés)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* ARTICLE 8 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 8 - Responsabilités et Limitations</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="font-bold text-red-800 mb-2">⚠️ Limitations de responsabilité</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Erreurs de calcul :</strong> Ovrio fournit des outils de calcul automatique, 
                    mais l'utilisateur reste seul responsable de la vérification et de l'exactitude des montants 
                    figurant sur ses devis et factures.
                  </li>
                  <li>
                    <strong>Conformité légale :</strong> L'utilisateur est responsable de s'assurer que ses documents 
                    respectent la législation applicable à son activité.
                  </li>
                  <li>
                    <strong>Perte de données :</strong> Bien que Ovrio effectue des sauvegardes régulières, 
                    nous recommandons d'effectuer vos propres exports de données.
                  </li>
                  <li>
                    <strong>Interruptions de service :</strong> Ovrio ne peut être tenu responsable des dommages 
                    résultant d'interruptions de service, sauf en cas de négligence grave.
                  </li>
                </ul>
              </div>

              <p>
                L'utilisateur s'engage à utiliser la Plateforme de manière conforme à sa destination et à ne pas 
                porter atteinte aux droits des tiers.
              </p>
            </div>
          </section>

          {/* ARTICLE 9 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 9 - Protection des Données Personnelles</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Le traitement de vos données personnelles est effectué conformément au Règlement Général sur la 
                Protection des Données (RGPD). Pour plus d'informations, consultez notre 
                <strong> Politique de Confidentialité</strong> dans les Mentions Légales.
              </p>

              <p>
                Vous disposez d'un droit d'accès, de rectification, de suppression et d'opposition concernant 
                vos données personnelles. Pour exercer ces droits, contactez-nous à : 
                <strong> contact@ovrio.net</strong>
              </p>
            </div>
          </section>

          {/* ARTICLE 10 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 10 - Modification des CGU/CGV</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Ovrio se réserve le droit de modifier les présentes CGU/CGV à tout moment. 
                Les utilisateurs seront informés de toute modification substantielle par email.
              </p>

              <p>
                La version en vigueur est celle accessible sur la Plateforme. Il est recommandé de consulter 
                régulièrement cette page.
              </p>

              <p>
                La poursuite de l'utilisation de la Plateforme après modification vaut acceptation des nouvelles conditions.
              </p>
            </div>
          </section>

          {/* ARTICLE 11 */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Article 11 - Loi Applicable et Juridiction</h2>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Les présentes CGU/CGV sont régies par le droit français.
              </p>

              <p>
                En cas de litige, les parties s'engagent à rechercher une solution amiable. 
                À défaut d'accord, le litige sera porté devant les tribunaux compétents de Paris.
              </p>

              <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded border border-slate-200">
                Conformément à l'article L.612-1 du Code de la consommation, en cas de litige, vous pouvez recourir 
                gratuitement à un médiateur de la consommation : 
                <strong> www.economie.gouv.fr/mediation-conso</strong>
              </p>
            </div>
          </section>

          {/* CONTACT */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Contact</h2>
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <p className="text-sm text-slate-700 mb-3">
                Pour toute question concernant ces CGU/CGV :
              </p>
              <div className="text-sm space-y-1">
                <p><strong>Email :</strong> <a href="mailto:contact@ovrio.net" className="text-blue-600 hover:underline">contact@ovrio.net</a></p>
                <p><strong>Réponse sous :</strong> 48 heures ouvrées</p>
              </div>
            </div>
          </section>

          {/* FOOTER */}
          <div className="mt-12 pt-6 border-t border-slate-200">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Shield size={14}/>
              <p>
                En utilisant Ovrio, vous acceptez ces conditions. 
                Dernière mise à jour : 12 décembre 2025
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CGUPage;
